'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import logPrintMapButtonClicked from '@alltrails/amplitude/events/logPrintMapButtonClicked';
import logError from '@alltrails/core/utils/logError';
import type { AllTrailsBounds } from '@alltrails/maps/types/Geo';
import type { User } from '@alltrails/types/User';
import type { PrintHost } from '../components/PrintModal/PrintModal';
import { useAppSelector } from '../redux';
import type { PrintSubject } from '../types/PrintSubject';
import preloadPrint from '../utils/preloadPrint';

type PrintModalComponent = typeof import('../components/PrintModal/PrintModal').default;

type UsePrintModalArgs = {
  autoOpen?: boolean;
  /** Frames the print. Null while the host has nothing printable yet, which cannot be opened. */
  bounds: AllTrailsBounds | null;
  host: PrintHost;
  /** Called when the modal closes, for a host that has to unwind whatever it opened it from */
  onClose?: () => void;
  onPrintRequested?: () => void;
  subject: PrintSubject;
};

const hasPrintMaps = (user?: User | null) => !!user?.featureEntitlements?.includes('print_maps');

/**
 * The print modal separated from whatever opens it: the chunk it pulls, the bounds it was opened
 * with, and the modal itself. Hosts with somewhere to put a control render PrintMapButton; hosts
 * that open the modal from elsewhere — a menu item, or a link with no control at all — drive this.
 * Bounds are captured when the modal opens so that panning behind it cannot reframe the preview.
 */
const usePrintModal = ({ autoOpen, bounds, host, onClose, onPrintRequested, subject }: UsePrintModalArgs) => {
  const [PrintModal, setPrintModal] = useState<PrintModalComponent | null>(null);
  const [printBounds, setPrintBounds] = useState<AllTrailsBounds | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUpOpen, setIsSignUpOpen] = useState(false);
  const [isUpsellOpen, setIsUpsellOpen] = useState(false);
  const [isOpenPending, setIsOpenPending] = useState(false);
  const hasAutoOpened = useRef(false);
  const hasSignedUp = useRef(false);
  const { mapLocation, renderSignUpModal, renderUpsellModal, trailId } = host;
  const { hasPrintEntitlement, isLoggedIn } = useAppSelector(state => ({
    hasPrintEntitlement: hasPrintMaps(state.context.currentUser),
    isLoggedIn: !!state.context.currentUser
  }));
  const isGated = !isLoggedIn || !hasPrintEntitlement;

  useEffect(() => {
    if (!hasPrintEntitlement || typeof window.requestIdleCallback !== 'function') {
      return undefined;
    }
    const handle = window.requestIdleCallback(() => {
      preloadPrint();
    });
    return () => window.cancelIdleCallback(handle);
  }, [hasPrintEntitlement]);

  const openModal = useCallback(async () => {
    if (!bounds) {
      return;
    }
    setIsLoading(true);
    try {
      const { default: Modal } = await import('../components/PrintModal/PrintModal');
      setPrintModal(() => Modal);
      setPrintBounds(bounds);
    } catch (error) {
      logError(new Error('Failed to load the print modal', { cause: error }));
      hasAutoOpened.current = false;
    } finally {
      setIsLoading(false);
    }
  }, [bounds]);

  const openOrGate = useCallback(() => {
    if (!isLoggedIn) {
      setIsSignUpOpen(true);
      return;
    }
    if (!hasPrintEntitlement) {
      setIsUpsellOpen(true);
      return;
    }
    openModal();
  }, [hasPrintEntitlement, isLoggedIn, openModal]);

  const openOnceFramed = useCallback(() => {
    if (bounds) {
      openModal();
    } else {
      setIsOpenPending(true);
    }
  }, [bounds, openModal]);

  useEffect(() => {
    if (isOpenPending && bounds) {
      setIsOpenPending(false);
      openModal();
    }
  }, [bounds, isOpenPending, openModal]);

  const open = useCallback(() => {
    if (!bounds) {
      return;
    }
    logPrintMapButtonClicked({ map_location: mapLocation, trail_id: trailId });
    onPrintRequested?.();
    openOrGate();
  }, [bounds, mapLocation, onPrintRequested, openOrGate, trailId]);

  useEffect(() => {
    if (!autoOpen) {
      hasAutoOpened.current = false;
      return;
    }
    if (hasAutoOpened.current || !(bounds || isGated)) {
      return;
    }
    hasAutoOpened.current = true;
    openOrGate();
  }, [autoOpen, bounds, isGated, openOrGate]);

  const printModal =
    PrintModal && printBounds ? (
      <PrintModal
        bounds={printBounds}
        host={host}
        onCloseRequest={() => {
          setPrintBounds(null);
          onClose?.();
        }}
        subject={subject}
      />
    ) : null;

  const renderGate = () => {
    if (isSignUpOpen) {
      return renderSignUpModal({
        onCloseRequest: () => {
          setIsSignUpOpen(false);
          if (!hasSignedUp.current) {
            onClose?.();
          }
        },
        onSuccess: user => {
          hasSignedUp.current = true;
          setIsSignUpOpen(false);
          if (hasPrintMaps(user)) {
            openOnceFramed();
          } else {
            setIsUpsellOpen(true);
          }
        }
      });
    }

    return renderUpsellModal({
      isOpen: true,
      onCloseRequest: () => {
        setIsUpsellOpen(false);
        if (hasPrintEntitlement) {
          openOnceFramed();
        } else {
          onClose?.();
        }
      }
    });
  };

  return {
    isLoading,
    modal: isSignUpOpen || isUpsellOpen ? renderGate() : printModal,
    open
  };
};

export default usePrintModal;
